// Tiny HTML templating + DOM morphing. No build step, no dependencies.
//
//   html`<p class="${cls}">${text}</p>`  -> SafeHTML (interpolations are escaped)
//   raw(str)                              -> mark a trusted string as HTML
//   morph(rootEl, safeHtml)               -> patch rootEl's children in place
//
// Arrays are joined, null/undefined/false render nothing. Attribute values are
// escaped the same way as text, so a template must always quote attributes.

export class SafeHTML {
  constructor(s) { this.s = s; }
  toString() { return this.s; }
}

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const esc = (v) => String(v).replace(/[&<>"']/g, (c) => ESC[c]);

function piece(v) {
  if (v === null || v === undefined || v === false || v === true) return '';
  if (v instanceof SafeHTML) return v.s;
  if (Array.isArray(v)) return v.map(piece).join('');
  return esc(v);
}

export function html(strings, ...vals) {
  let out = strings[0];
  for (let i = 0; i < vals.length; i++) out += piece(vals[i]) + strings[i + 1];
  return new SafeHTML(out);
}

export const raw = (s) => new SafeHTML(String(s == null ? '' : s));
export const when = (cond, a, b) => (cond ? (typeof a === 'function' ? a() : a) : (b === undefined ? '' : (typeof b === 'function' ? b() : b)));
// Conditional attribute helper: attr('disabled', isDisabled)
export const attr = (name, on, value) => (on ? raw(` ${name}${value === undefined ? '' : `="${esc(value)}"`}`) : '');

// ---------------------------------------------------------------- morph
const tpl = typeof document !== 'undefined' ? document.createElement('template') : null;

export function morph(root, content) {
  tpl.innerHTML = String(content);
  patchChildren(root, tpl.content);
}

function keyOf(n) {
  return n.nodeType === 1 ? n.getAttribute('data-key') : null;
}

function sameKind(a, b) {
  if (a.nodeType !== b.nodeType) return false;
  if (a.nodeType === 1) return a.nodeName === b.nodeName && keyOf(a) === keyOf(b);
  return true;
}

function patchChildren(oldP, newP) {
  const oldKids = Array.from(oldP.childNodes);
  const newKids = Array.from(newP.childNodes);
  const keyed = new Map();
  for (const o of oldKids) {
    const k = keyOf(o);
    if (k !== null) keyed.set(k, o);
  }
  let oi = 0;
  const used = new Set();
  const result = [];
  for (const n of newKids) {
    const k = keyOf(n);
    let match = null;
    if (k !== null) {
      const cand = keyed.get(k);
      if (cand && !used.has(cand) && cand.nodeName === n.nodeName) match = cand;
    } else {
      // positional match among remaining unkeyed nodes
      while (oi < oldKids.length && (used.has(oldKids[oi]) || keyOf(oldKids[oi]) !== null)) oi++;
      if (oi < oldKids.length && sameKind(oldKids[oi], n)) { match = oldKids[oi]; oi++; }
    }
    if (match) {
      used.add(match);
      patchNode(match, n);
      result.push(match);
    } else {
      result.push(n);
    }
  }
  for (const o of oldKids) if (!used.has(o)) o.remove();
  // place nodes in order
  for (let i = 0; i < result.length; i++) {
    const want = result[i];
    const cur = oldP.childNodes[i];
    if (cur !== want) oldP.insertBefore(want, cur || null);
  }
}

function patchNode(o, n) {
  if (o.nodeType === 3 || o.nodeType === 8) {
    if (o.nodeValue !== n.nodeValue) o.nodeValue = n.nodeValue;
    return;
  }
  if (o.nodeType !== 1) return;
  // attributes
  const oa = o.attributes, na = n.attributes;
  for (let i = oa.length - 1; i >= 0; i--) {
    const a = oa[i].name;
    if (!n.hasAttribute(a)) o.removeAttribute(a);
  }
  for (let i = 0; i < na.length; i++) {
    const { name, value } = na[i];
    if (o.getAttribute(name) !== value) o.setAttribute(name, value);
  }
  const tag = o.nodeName;
  if (tag === 'INPUT') {
    const focused = document.activeElement === o;
    if (o.type === 'checkbox' || o.type === 'radio') o.checked = n.hasAttribute('checked');
    else if (!focused) { const v = n.getAttribute('value') || ''; if (o.value !== v) o.value = v; }
    return;
  }
  if (tag === 'TEXTAREA') {
    const focused = document.activeElement === o;
    if (!focused && o.value !== n.value) o.value = n.value;
    return;
  }
  if (o.hasAttribute('data-morph-skip')) return;
  patchChildren(o, n);
  if (tag === 'SELECT') {
    const sel = n.querySelector('option[selected]');
    if (sel) o.value = sel.value;
  }
}
